import React, {useEffect, useState} from 'react';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import {isOk} from '../../../utils/apiResponse';
import {SpinnerButton} from '../../SpinnerButton';
import {MercadoAdsErrorView} from './MercadoAdsErrorView';
import {selectMercadoAdsAdvertisers} from './mercadoAdsApi';
import {advertiserText, errorCodeOf, flagOf, nicknameOf} from './mercadoAds';

function idOf(advertiser) {
    return String(advertiser.advertiser_id);
}

/**
 * La elección de anunciantes del Portal (contrato §3 y §6.7, `copy.md` §6): un vendedor con publicidad
 * en más de un país elige cuáles ve su agencia. Arrancan marcados todos los que todavía no comparte:
 * conectar es para compartir, y desmarcar uno es la excepción.
 *
 * Los que YA comparte se ven marcados y fijos, con «Ya la ve tu agencia»: compartir es aditivo en el
 * backend (`link_selected_advertisers` no desvincula nada), así que dejarlos desmarcar prometía un cambio
 * que no ocurría. Para dejar de compartir, el cliente desconecta la cuenta.
 *
 * `result` es lo que dejó el callback (`null` = cerrado). `onClose(shared)` avisa si se compartió.
 * `static`: un clic afuera no descarta la lista (habría que repetir el OAuth); cierran la X y Escape, y
 * la tarjeta del Portal ofrece volver a abrirla mientras no se haya compartido nada.
 */
export function MercadoAdsSelectModal({result, activeClientId, onClose, select = selectMercadoAdsAdvertisers}) {
    const {t} = useTranslation();
    const advertisers = (result && result.advertisers) || [];
    const linked = ((result && result.linkedAdvertiserIds) || []).map(String);
    const [chosen, setChosen] = useState([]);
    const [sharing, setSharing] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!result) return;
        const alreadyLinked = (result.linkedAdvertiserIds || []).map(String);
        setChosen((result.advertisers || []).map(idOf).filter((id) => !alreadyLinked.includes(id)));
        setError(null);
        setSharing(false);
    }, [result]);

    const toggle = (id) => setChosen((current) => (
        current.includes(id) ? current.filter((each) => each !== id) : [...current, id]));

    const share = () => {
        if (sharing || chosen.length === 0) return;
        setSharing(true);
        setError(null);
        // Los ids van como los mandó el backend (número), no como el texto del checkbox.
        const ids = advertisers.filter((advertiser) => chosen.includes(idOf(advertiser)))
            .map((advertiser) => advertiser.advertiser_id);
        select(ids, activeClientId, result.credentialId).then((response) => {
            setSharing(false);
            if (isOk(response)) {
                onClose(true);
                return;
            }
            setError({code: errorCodeOf(response), nickname: nicknameOf(response)});
        }).catch(() => {
            setSharing(false);
            setError({code: errorCodeOf(null), nickname: null});
        });
    };

    return (
        <Modal isOpen={!!result} toggle={() => onClose(false)} backdrop="static" className="ma-select-modal">
            <ModalHeader toggle={() => onClose(false)}>{t('portal.connections.mercado_ads.select_title')}</ModalHeader>
            <ModalBody>
                <div className="modal-section ma-select" data-testid="mercado-ads-select">
                    <span className="ma-select__help">{t('portal.connections.mercado_ads.select_help')}</span>
                    <ul className="ma-select__list">
                        {advertisers.map((advertiser) => {
                            const id = idOf(advertiser);
                            const flag = flagOf(advertiser);
                            const isLinked = linked.includes(id);
                            return (
                                <li key={id}>
                                    <label className={`ma-select__item${isLinked ? ' -linked' : ''}`}
                                           data-testid={`mercado-ads-select-${id}`}>
                                        <input type="checkbox" checked={isLinked || chosen.includes(id)}
                                               disabled={sharing || isLinked} onChange={() => toggle(id)}/>
                                        {flag && <span className="ma-select__flag" aria-hidden="true">{flag}</span>}
                                        <span className="ma-select__name">{advertiserText(t, advertiser)}</span>
                                        {isLinked && (
                                            <span className="ma-select__shared">{t('portal.connections.mercado_ads.already_shared')}</span>
                                        )}
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                    {error && <MercadoAdsErrorView code={error.code} nickname={error.nickname}/>}
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn -alt" disabled={chosen.length === 0 || sharing}
                        aria-busy={sharing || undefined} onClick={share} data-testid="mercado-ads-select-share">
                    {sharing && <SpinnerButton/>}
                    {t('portal.connections.mercado_ads.select_confirm')}
                </button>
            </ModalFooter>
        </Modal>
    );
}

export default MercadoAdsSelectModal;
